// Session API client shared by the console (cookie session) and the apps (bearer token). In the
// apps it also works offline (shared/offline.ts, PMU-270): a GET that cannot reach the server
// answers with what was last loaded, and a write marked `queue` waits on the device instead of
// failing. Every app write carries an Idempotency-Key, so sending it again is safe.
import { noteError } from "./errorlog.ts";
import { cacheGet, cachePut, blobGet, blobPut, enqueueWrite, markOffline, markOnline, offlineEnabled, offlineState, type QueuedWrite } from "./offline.ts";
export class ApiError extends Error {
  status: number;
  code: string;
  /** The error body's other fields (e.g. which children are still on the bus). */
  data?: Record<string, unknown>;
  constructor(status: number, code: string, message?: string, data?: Record<string, unknown>) { super(message ?? code); this.status = status; this.code = code; this.data = data; }
}

export interface ApiConfig {
  base: string; // "" on the web (same origin); the API origin in the apps
  getToken?: () => string | null; // apps only
  onUnauthenticated?: () => void;
}

let config: ApiConfig = { base: "" };
export const configureApi = (c: ApiConfig) => { config = c; };
export const apiBase = () => config.base;

/** Offline options for one call (the apps only; ignored in the console). */
export interface ApiOpts {
  raw?: Blob | Uint8Array; type?: string;
  /** A write that may wait offline: what it is (for the person, if it is refused later) and what to answer now. */
  queue?: { label: string; result?: unknown; doneIf?: string[]; dropIf?: string[] };
  /** The write's Idempotency-Key (a new one by default). */
  key?: string;
  /** Extra request headers (e.g. X-Filename for a raw upload). */
  headers?: Record<string, string>;
}

/** Thrown when the device is offline and the call has no saved answer and cannot wait. */
export const OFFLINE = "offline";

/**
 * With the internet down but the Wi-Fi up, a request can hang (DNS and connects time out slowly)
 * instead of failing at once as in airplane mode, and the screen waits on it. The apps give up
 * after a while, sooner once they know they are offline, and use what was kept.
 */
const WAIT_ONLINE_MS = 10_000;
const WAIT_OFFLINE_MS = 4_000;

async function request(method: string, path: string, body: unknown, opts: ApiOpts, key: string | null): Promise<Response> {
  if (!offlineEnabled() || opts.raw) return send(method, path, body, opts, key);
  // A race, not an abort: the native shells' fetch (CapacitorHttp) does not honour abort signals.
  // A write that did arrive late is harmless: it is queued with the same Idempotency-Key.
  let t: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<never>((_, reject) => { t = setTimeout(() => reject(new TypeError("timed out")), offlineState().online ? WAIT_ONLINE_MS : WAIT_OFFLINE_MS); });
  try { return await Promise.race([send(method, path, body, opts, key), late]); } finally { clearTimeout(t); }
}

function send(method: string, path: string, body: unknown, opts: ApiOpts, key: string | null): Promise<Response> {
  const headers: Record<string, string> = { ...opts.headers, "X-PMU-Request": "1" };
  const token = config.getToken?.();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (key) headers["Idempotency-Key"] = key;
  if (opts.raw) headers["Content-Type"] = opts.type ?? "application/octet-stream";
  else if (body !== undefined) headers["Content-Type"] = "application/json";
  return fetch(`${config.base}${path}`, {
    method, headers, credentials: config.base ? "omit" : "same-origin",
    body: opts.raw ? (opts.raw as BodyInit) : body === undefined ? undefined : JSON.stringify(body),
  });
}

/** Sends a write that waited offline, with its own key (shared/offline.ts calls this). */
export const sendQueued = (w: QueuedWrite) => request(w.method, w.path, w.body, {}, w.key);

export async function api<T = any>(method: string, path: string, body?: unknown, opts: ApiOpts = {}): Promise<T> {
  const offline = offlineEnabled();
  const key = offline && method !== "GET" && !opts.raw ? (opts.key ?? crypto.randomUUID()) : null;
  let res: Response;
  try {
    res = await request(method, path, body, opts, key);
  } catch (e) {
    // No network (fetch throws; an HTTP error never does).
    if (!offline) throw e;
    markOffline();
    if (method === "GET") {
      const hit = await cacheGet<T>(path);
      if (hit) return hit.data;
    } else if (opts.queue && key) {
      await enqueueWrite({ key, method, path, body, label: opts.queue.label, at: Date.now(), ...(opts.queue.doneIf ? { doneIf: opts.queue.doneIf } : {}), ...(opts.queue.dropIf ? { dropIf: opts.queue.dropIf } : {}) });
      return (opts.queue.result ?? { queued: true }) as T;
    }
    throw new ApiError(0, OFFLINE, "You are offline. This will work again when the internet is back.");
  }
  if (offline) markOnline();
  const text = await res.text();
  let data: any = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    if (res.status === 401) config.onUnauthenticated?.();
    else noteError(`${method} ${path.split("?")[0]} ${res.status} ${data?.error ?? ""}`);
    throw new ApiError(res.status, data?.error ?? "error", data?.message ?? friendly(data?.error), data ?? undefined);
  }
  if (offline && method === "GET") void cachePut(path, data);
  return data as T;
}

/** Fetches an authorised blob (photo, signature strokes); in the apps, kept for offline. */
export async function blobData(path: string): Promise<Blob> {
  const token = config.getToken?.();
  let res: Response;
  try {
    res = await fetch(`${config.base}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: config.base ? "omit" : "same-origin" });
  } catch (e) {
    const kept = offlineEnabled() ? await blobGet(path) : null;
    if (kept) { markOffline(); return kept; }
    throw e;
  }
  if (!res.ok) throw new ApiError(res.status, "blob");
  const blob = await res.blob();
  if (offlineEnabled()) void blobPut(path, blob);
  return blob;
}

/** Fetches an authorised blob as an object URL. */
export async function blobUrl(path: string): Promise<string> {
  return URL.createObjectURL(await blobData(path));
}

const MESSAGES: Record<string, string> = {
  unauthenticated: "Please sign in again.",
  forbidden: "You do not have access to that.",
  mfa_required: "Use your second factor to continue.",
  not_found: "That could not be found.",
  bad_credentials: "Email or password is wrong.",
  internal: "Something went wrong on our side. Try again in a minute.",
};
export const friendly = (code?: string) => (code ? MESSAGES[code] ?? code.replace(/_/g, " ") : "Something went wrong.");
